
import Vue from "vue";
import store from "@/store";
import { APPLICATION } from "@/store/applications";
import { createNamespacedHelpers } from "vuex";
import { IApplicationState } from "@/store/applications/types";
import { requiredIf } from "vuelidate/lib/validators";
import { format } from "date-fns";
import { DATE_FORMAT } from "@/constants/dateFormat";
import { ADDRESS_LOOKUP, emptyAddress } from "@/store/addressLookup";
import { IAddressLookupResponse } from "@/store/addressLookup/types";

const { mapGetters, mapState } = createNamespacedHelpers(
  APPLICATION.NAME_SPACE
);

export default Vue.extend({
  data() {
    return {
      emptyAddress
    };
  },
  watch: {
    "$v.$invalid": "setValidity"
  },
  computed: {
    genderLabel() {
      // In the front end form we convert NonBinary to Undisclosed
      switch (this.gender) {
        case "UnDisclosed":
          return "Undisclosed/Non Binary";
        default:
          return this.gender;
      }
    },
    residentialAddressLookupResponse(): IAddressLookupResponse {
      return this.$store.state.addressLookup.responses.residential as any;
    },
    residentialAddressLookupInProgress(): boolean {
      return this.$store.state.addressLookup.progress.residential.inProgress;
    },
    residentialAddress: {
      get(): any {
        const residentialAddress =
          store.state.application.applicationData &&
          store.state.application.applicationData.creditDetails &&
          store.state.application.applicationData.creditDetails
            .residentialAddress;

        if (residentialAddress && residentialAddress.fullAddress) {
          return residentialAddress;
        }
        return this.residentialAddressText || emptyAddress;
      },
      set(value: string) {
        // if the value is a string it's a manual address
        const isManualAddress = typeof value === "string";
        const newValue = isManualAddress ? emptyAddress : value;

        this.$store.commit(
          APPLICATION.NS(APPLICATION.MUTATIONS.SET_RESIDENTIAL_ADDRESS),
          newValue
        );

        this.$store.commit(
          APPLICATION.NS(
            APPLICATION.MUTATIONS.SET_HAS_MANUAL_RESIDENTIAL_ADDRESS
          ),
          isManualAddress
        );
      }
    },
    residentialAddressText: {
      get(): string {
        return (
          (store.state.application.applicationData &&
            store.state.application.applicationData.creditDetails &&
            store.state.application.applicationData.creditDetails
              .residentialAddressText) ||
          ""
        );
      },
      set(value: string) {
        this.$store.commit(
          APPLICATION.NS(APPLICATION.MUTATIONS.SET_RESIDENTIAL_ADDRESS_TEXT),
          value
        );
      }
    },
    hasManualResidentialAddress: {
      get(): boolean {
        return (
          (store.state.application.applicationData &&
            store.state.application.applicationData.creditDetails &&
            store.state.application.applicationData.creditDetails
              .hasManualResidentialAddress) ||
          false
        );
      },
      set(value: boolean) {
        this.$store.commit(
          APPLICATION.NS(
            APPLICATION.MUTATIONS.SET_HAS_MANUAL_RESIDENTIAL_ADDRESS
          ),
          value
        );
      }
    },
    ...mapState({
      businessType: (state: IApplicationState) =>
        state.applicationData &&
        state.applicationData.businessDetails &&
        state.applicationData.businessDetails.businessType,
      isCompanyDirector: (state: IApplicationState) =>
        state.applicationData &&
        state.applicationData.creditDetails &&
        state.applicationData.creditDetails.isCompanyDirector,
      gender: (state: IApplicationState) =>
        state.applicationData &&
        state.applicationData.creditDetails &&
        state.applicationData.creditDetails.gender,
      dateOfBirth: (state: IApplicationState) =>
        state.applicationData &&
        state.applicationData.creditDetails &&
        state.applicationData.creditDetails.dateOfBirth &&
        format(state.applicationData.creditDetails.dateOfBirth, DATE_FORMAT),
      driversLicenseNumber: (state: IApplicationState) =>
        state.applicationData &&
        state.applicationData.creditDetails &&
        state.applicationData.creditDetails.driversLicenseNumber,
      driversLicenseVersionNo: (state: IApplicationState) =>
        state.applicationData &&
        state.applicationData.creditDetails &&
        state.applicationData.creditDetails.driversLicenseVersionNo,
      legalName: (state: IApplicationState) =>
        state.applicationData &&
        state.applicationData.creditDetails &&
        state.applicationData.creditDetails.legalName
    }),
    ...mapGetters([APPLICATION.GETTERS.IS_EDITABLE])
  },
  methods: {
    setValidity() {
      this.$emit("isvalid", "credit", !(this as any).$v.$invalid);
    },
    onResidentialAddressSearch(address: string) {
      this.$store.dispatch(
        ADDRESS_LOOKUP.NS(ADDRESS_LOOKUP.ACTIONS.ADDRESS_LOOKUP_BY_KEY),
        {
          key: "residential",
          address
        }
      );
    },
    onChangeResidentialAddressText(value: string) {
      if (value) {
        this.$store.commit(
          APPLICATION.NS(APPLICATION.MUTATIONS.SET_RESIDENTIAL_ADDRESS_TEXT),
          value
        );
      }
    },
    onChangeHasManualResidentialAddress() {
      this.$store.commit(
        APPLICATION.NS(
          APPLICATION.MUTATIONS.SET_HAS_MANUAL_RESIDENTIAL_ADDRESS
        ),
        true
      );

      // reset the delivery address object to blank state
      // TODO: figure out that this should actually do
      this.$store.commit(
        APPLICATION.NS(APPLICATION.MUTATIONS.SET_RESIDENTIAL_ADDRESS),
        emptyAddress
      );
    }
  },
  validations: {
    residentialAddressText: {
      required: requiredIf(
        vm => vm.hasManualResidentialAddress && vm.isEditable
      )
    }
  }
});
